import { FC, useMemo, useState, SetStateAction, Dispatch } from 'react';

import dayjs from 'dayjs';

import Button from '@/components/ui/Buttons/Button';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import CalendarDatePicker from '@/components/ui/CalendarDatePicker/CalendarDatePicker';
import HeaderText, { HeaderSize } from '@/components/ui/HeaderText/HeaderText';
import { Modal } from '@/components/ui/Modal';

import { TypeSubEngineInstallment } from '@/lib/api/subscriptionManager/types';

import { CTA_NAMES } from '@/utils/rudderstack';
import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';

import { trackClickEvent } from '../../../../../utils';

export interface EditShipDateProps {
  t: TypeAccountERCTranslations;
  installment: TypeSubEngineInstallment;
  setInstallment: Dispatch<SetStateAction<TypeSubEngineInstallment>>;
}

const EditShipDate: FC<EditShipDateProps> = ({
  t,
  installment,
  setInstallment,
}) => {
  const {
    scheduled_date: scheduledDate,
    original_scheduled_date: originalScheduledDate,
    id: installmentId,
  } = installment;

  const currentScheduledDate = useMemo(
    () => dayjs(scheduledDate).toDate(),
    [scheduledDate],
  );

  const [rangeStart, rangeEnd] = useMemo(() => {
    const originalScheduledDateAsDate = dayjs(originalScheduledDate);

    const minDateRangeByWeeks = originalScheduledDateAsDate.subtract(3, 'week');

    const today = dayjs();

    const rangeStartDate = minDateRangeByWeeks.isBefore(today)
      ? today.add(1, 'day').toDate()
      : minDateRangeByWeeks.toDate();

    return [
      rangeStartDate,
      originalScheduledDateAsDate.add(3, 'week').toDate(),
    ];
  }, [originalScheduledDate]);

  const [modal, setModal] = useState(false);

  const [newShipDate, setNewShipDate] = useState(currentScheduledDate);

  const handleSave = async () => {
    try {
      const response = await fetch('/api/update-installment', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          installmentId,
          installment: {
            scheduled_date: dayjs(newShipDate).format('YYYY-MM-DD'),
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`${response.status} (${response.statusText})`);
      }

      const data = await response.json();

      setInstallment(data.installment);

      setModal(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} data-testid="editShipDate">
      <Button
        classes="text-sm md:text-base font-bold"
        size={BUTTON_SIZES.none}
        style={BUTTON_STYLES.link}
        ctaText={t.edit}
        onClick={() => {
          trackClickEvent(CTA_NAMES.EDIT_SHIP_DATE);

          setModal(true);
        }}
      />

      <Modal
        modalClasses={{
          mobile: {
            padding: '16px 16px 35px',
            borderRadius: '6px',
          },
          desktop: {
            padding: '32px',
            borderRadius: '6px',
          },
        }}
        contentClasses="grid gap-y-2.5 md:gap-y-4"
        isOpen={modal}
        handleClose={() => {
          setModal(false);

          setNewShipDate(currentScheduledDate);
        }}
      >
        <div className="grid gap-y-4">
          <div className="grid gap-y-1">
            <HeaderText
              size={HeaderSize.H4}
              classes="font-bold"
              text={t.editShipDateTitle}
              hoverEffect
            />

            <p>{t.editShipDateSubText}</p>
          </div>

          <hr className="border-grey-midtone" />

          <CalendarDatePicker
            selected={newShipDate}
            onChange={setNewShipDate}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            highlightDates={[currentScheduledDate]}
          />

          <hr className="border-grey-midtone" />

          <Button
            classes="h-12 md:h-16"
            size={BUTTON_SIZES.none}
            style={BUTTON_STYLES.primary}
            ctaText={t.save}
            onClick={handleSave}
          />
        </div>
      </Modal>
    </div>
  );
};

export default EditShipDate;
